// Login Referral Popup — appears once per login session after successful login.
// Reads runtime settings from GET /api/campaign/config so admins can toggle it
// on/off, tune the cooldown, and rewrite the headline / share copy without a
// redeploy.
import { useEffect, useMemo, useState } from "react";
import { Gift, X, Share2, Copy, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Analytics } from "../lib/analytics";
import { referralWhatsappMessage } from "../lib/referralMessage";
import { Button } from "./ui/button";

const KEY_LAST_SHOWN = "pmp.referralPopup.lastShownAt";
const DEFAULT_COOLDOWN_HOURS = 24;

export function shouldShowLoginReferralPopup(cooldownHours = DEFAULT_COOLDOWN_HOURS) {
  try {
    if (!cooldownHours || cooldownHours <= 0) return true;
    const t = Number(localStorage.getItem(KEY_LAST_SHOWN) || 0);
    if (!t) return true;
    return Date.now() - t > cooldownHours * 3_600_000;
  } catch { return true; }
}

export function markLoginReferralPopupShown() {
  try { localStorage.setItem(KEY_LAST_SHOWN, String(Date.now())); } catch {}
}

export default function LoginReferralPopup({ open, onClose }) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [r, cfg] = await Promise.all([
          api.referralsMy(),
          api.campaignConfig().catch(() => null),
        ]);
        if (!cancelled) { setData(r); setConfig(cfg); }
        Analytics.track("REFERRAL_POPUP_SHOWN", { source: "login" });
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const successful = data?.stats?.successful ?? 0;
  const milestone = config?.milestone ?? data?.milestone ?? 50;
  const pct = Math.min(100, Math.round((successful / milestone) * 100));
  const shareLink = data?.share_link || "";
  const headline = config?.popup_headline || "🎁 50 TEACHERS REFER କରନ୍ତୁ — 1 YEAR PREMIUM + 6 MONTH RECHARGE! 🚀";
  const subline = config?.popup_subline || "Complete 50 successful Premium activations — registration alone doesn't count.";
  const rewardDays = config?.reward_days ?? 365;
  const rechargeMonths = config?.recharge_months ?? 6;
  const template = config?.share_message; // may be null → helper falls back to default
  const msg = useMemo(() => (shareLink ? referralWhatsappMessage(shareLink, template) : ""), [shareLink, template]);

  if (!open) return null;

  const close = (reason = "manual") => {
    Analytics.track("REFERRAL_POPUP_CLOSED", { reason });
    onClose && onClose();
  };

  const doShare = async () => {
    if (!shareLink) return;
    Analytics.track("REFERRAL_CTA_CLICKED", { source: "login_popup" });
    try {
      if (navigator.share) {
        await navigator.share({ title: "PM POSHAN Calculator", text: msg });
        Analytics.track("REFERRAL_WHATSAPP_SHARED", { via: "native_share" });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    Analytics.track("REFERRAL_WHATSAPP_SHARED", { via: "wa_me" });
  };

  const doCopy = async () => {
    if (!shareLink) return;
    setCopying(true);
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Referral link copied");
      Analytics.track("REFERRAL_LINK_COPIED", { source: "login_popup" });
    } catch {
      toast.error("Could not copy — long-press the link instead");
    } finally { setCopying(false); }
  };

  const rewardYears = Math.floor(rewardDays / 365);
  const premiumLabel = rewardYears >= 1 ? `${rewardYears} Year${rewardYears > 1 ? "s" : ""} Premium` : `${rewardDays} Days Premium`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
         data-testid="login-referral-popup" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="relative bg-gradient-to-br from-orange-500 via-amber-500 to-emerald-600 text-white px-5 pt-5 pb-16">
          <button aria-label="Close" onClick={() => close("close_button")}
                  className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                  data-testid="popup-close">
            <X size={18}/>
          </button>
          <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] opacity-90">
            <Gift size={16}/> Refer &amp; Earn
          </div>
          <h2 className="mt-2 font-display font-black text-2xl leading-tight whitespace-pre-line" data-testid="popup-headline">
            {headline}
          </h2>
          <p className="mt-2 text-sm text-white/90">{subline}</p>
        </div>

        <div className="-mt-10 mx-4 mb-4 rounded-2xl bg-white border border-slate-200 shadow-md p-5">
          <div className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-500">
            <span>🎯 Your Progress</span>
            <span data-testid="popup-progress">{successful} / {milestone}</span>
          </div>
          <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-orange-500 to-emerald-600 transition-all"
                 style={{ width: `${pct}%` }} data-testid="popup-progress-bar"/>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            {Math.max(0, milestone - successful)} more <b>successful Premium activation{milestone - successful === 1 ? "" : "s"}</b> to unlock rewards
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-center text-xs">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-2">
              <div className="text-lg">💎</div>
              <div className="font-bold text-emerald-900">{premiumLabel}</div>
              <div className="text-emerald-700/80">FREE</div>
            </div>
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-2">
              <div className="text-lg">📱</div>
              <div className="font-bold text-orange-900">{rechargeMonths} Month Recharge</div>
              <div className="text-orange-700/80">Any operator</div>
            </div>
          </div>

          <Button onClick={doShare} disabled={loading || !shareLink}
                  className="mt-5 w-full min-h-[52px] bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-black text-base tracking-wide shadow-lg animate-pulse-shine"
                  data-testid="popup-share-now">
            {loading ? <Loader2 className="animate-spin" size={18}/> : <Send size={18}/>}
            🚀 SHARE NOW — ଏବେ ହିଁ SHARE କରନ୍ତୁ
          </Button>

          <div className="mt-3 flex gap-2">
            <Button variant="outline" onClick={doCopy} disabled={copying || !shareLink}
                    className="flex-1 min-h-[44px]" data-testid="popup-copy">
              <Copy size={14}/> {copying ? "Copying…" : "Copy Link"}
            </Button>
            <Button variant="outline" onClick={() => { close("view_page"); window.location.assign("/referrals"); }}
                    className="flex-1 min-h-[44px]" data-testid="popup-view-page">
              <Share2 size={14}/> View Refer &amp; Earn
            </Button>
          </div>

          <div className="mt-3 flex justify-between text-[11px] text-slate-500">
            <a href="/referrals#terms" onClick={() => close("terms_link")} className="underline">Referral terms</a>
            <button onClick={() => close("later")} className="underline" data-testid="popup-later">Later</button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulseShine { 0%{box-shadow:0 0 0 0 rgba(16,185,129,.55)} 60%{box-shadow:0 0 0 12px rgba(16,185,129,0)} 100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} }
        .animate-pulse-shine { animation: pulseShine 1.8s infinite; }
      `}</style>
    </div>
  );
}
